// The OPIN applications calls the admin SPA makes, on the `HttpClient` with the browser session
// cookie: the list, create, secret rotation, edit, enable and disable routes of Power, and the
// operator's role from the session. Every answer is decoded with its closed admin contract; a
// refusal keeps its status, the Problem `detail` the screen shows, and the error code and trace id
// to quote to support, never retried as a throw.
import { Array as Arr, Duration, Effect, Option, Schema, Struct } from 'effect';
import { FetchHttpClient, HttpBody, HttpClient, HttpClientRequest } from 'effect/http';
import { dual } from 'effect/Function';
import { FAPI_HEADERS } from '@finnest/core/effect';
import {
  OPIN_APPLICATION_COMMAND_REFUSALS,
  OpinApplicationList,
  OpinApplicationSecretIssued,
  OpinApplicationView,
} from '@finnest/power-admin-contracts';
import { PowerRefusal, powerRefusalOf } from '@finnest/power-admin-contracts/failure';
import type {
  OpinApplicationCreateBody,
  OpinApplicationIdempotencyKey,
  OpinApplicationList as OpinApplicationListType,
  OpinApplicationSecretIssued as OpinApplicationSecretIssuedType,
  OpinApplicationUpdateBody,
  OpinApplicationView as OpinApplicationViewType,
} from '@finnest/power-admin-contracts';

/** Why an applications call did not succeed, as the screen explains it. */
export const ApplicationsFailureReason = Schema.Literals([
  'unauthenticated',
  'forbidden',
  'not-found',
  'conflict',
  'key-reused',
  'rejected',
  'unavailable',
  'invalid-reply',
  'failed',
  'timed-out',
]);
export type ApplicationsFailureReason = typeof ApplicationsFailureReason.Type;

export class ApplicationsRequestFailed extends Schema.TaggedError<ApplicationsRequestFailed>()(
  'ApplicationsRequestFailed',
  {
    reason: ApplicationsFailureReason,
    detail: Schema.Option(Schema.String),
    /** The error code and trace id the answer names, to quote to support; none without an answer. */
    refusal: Schema.NullOr(PowerRefusal),
    /** What failed underneath: the transport, body or decoding error, when there was one. */
    cause: Schema.Option(Schema.instanceOf(Error)),
  },
) {
  override get message(): string {
    return `OPIN applications request failed: ${this.reason}`;
  }
}

/** The error code and trace id a failed call names, to quote to support; none without an answer. */
export const failureRefusal = (failure: ApplicationsRequestFailed): Option.Option<PowerRefusal> =>
  Option.fromNullOr(failure.refusal);

/** Which of Power's create and rotation refusals a failure is, by its Problem `detail`. */
export type CommandRefusal = keyof typeof OPIN_APPLICATION_COMMAND_REFUSALS;

const COMMAND_REFUSALS = Struct.keys(OPIN_APPLICATION_COMMAND_REFUSALS);

/** The create or rotation refusal `failure` carries, matched on Power's own `detail` text. */
export const commandRefusalOf = (
  failure: ApplicationsRequestFailed,
): Option.Option<CommandRefusal> =>
  Option.flatMap(failure.detail, (detail) =>
    Arr.findFirst(
      COMMAND_REFUSALS,
      (refusal) => OPIN_APPLICATION_COMMAND_REFUSALS[refusal] === detail,
    ),
  );

/** One applications call to this deployment's Power API. */
export type ApplicationsCall<A> = Effect.Effect<
  A,
  ApplicationsRequestFailed,
  HttpClient.HttpClient
>;

const APPLICATIONS_PATH = '/power/admin/opin/applications';
const SESSION_PATH = '/api/auth/get-session';
// A read never waits longer than this.
const READ_BUDGET_SECONDS = 15;
const READ_BUDGET = Duration.seconds(READ_BUDGET_SECONDS);
// A write waits far longer, and its end is never read as a failure: the screen says the write
// may have happened and has the operator check the list (or rotate again) before retrying.
const WRITE_DEADLINE_SECONDS = 60;
const WRITE_DEADLINE = Duration.seconds(WRITE_DEADLINE_SECONDS);
const HTTP_SUCCESS_FLOOR = 200;
const HTTP_SUCCESS_CEILING = 299;
const HTTP_BAD_REQUEST = 400;
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_CONFLICT = 409;
const HTTP_UNPROCESSABLE = 422;
const HTTP_UNAVAILABLE = 503;
const CLOSED = { onExcessProperty: 'error' } as const;

const REASON_BY_STATUS: ReadonlyMap<number, ApplicationsFailureReason> = new Map([
  [HTTP_BAD_REQUEST, 'rejected'],
  [HTTP_UNAUTHORIZED, 'unauthenticated'],
  [HTTP_FORBIDDEN, 'forbidden'],
  [HTTP_NOT_FOUND, 'not-found'],
  [HTTP_CONFLICT, 'conflict'],
  [HTTP_UNPROCESSABLE, 'key-reused'],
  [HTTP_UNAVAILABLE, 'unavailable'],
]);

// RFC 7807 answers carry what went wrong in `detail`; the other members are not read.
const decodeProblemDetail = Schema.decodeUnknownOption(
  Schema.fromJsonString(Schema.Struct({ detail: Schema.NonEmptyString })),
);

// The Power session body is the operator tools' enriched session; only its scopes are read: what
// Power IAM's roles.ts grants the member's role, `*` granting everything.
const decodeSessionScopes = Schema.decodeEffect(
  Schema.fromJsonString(Schema.Struct({ scopes: Schema.Array(Schema.String) })),
);

const failed = (reason: ApplicationsFailureReason): ApplicationsRequestFailed =>
  ApplicationsRequestFailed.make({
    reason,
    detail: Option.none(),
    refusal: null,
    cause: Option.none(),
  });

const failedBy =
  (reason: ApplicationsFailureReason) =>
  (cause: Error): ApplicationsRequestFailed =>
    ApplicationsRequestFailed.make({
      reason,
      detail: Option.none(),
      refusal: null,
      cause: Option.some(cause),
    });

// A refusal Power answered: its Problem `detail` and the code and trace id it names.
const refusedWith = (reason: ApplicationsFailureReason, text: string): ApplicationsRequestFailed =>
  ApplicationsRequestFailed.make({
    reason,
    detail: Option.map(decodeProblemDetail(text), (problem) => problem.detail),
    refusal: Option.getOrNull(powerRefusalOf(text)),
    cause: Option.none(),
  });

// The answer's text on 2xx, otherwise the reason its status names with the refusal's code and trace.
const answeredText = (request: HttpClientRequest.HttpClientRequest): ApplicationsCall<string> =>
  Effect.gen(function* () {
    const client = yield* HttpClient.HttpClient;
    const response = yield* client
      .execute(HttpClientRequest.acceptJson(request))
      .pipe(Effect.mapError(failedBy('failed')));
    const answered =
      response.status >= HTTP_SUCCESS_FLOOR && response.status <= HTTP_SUCCESS_CEILING;
    // A refusal's status gives the reason; a body that cannot be read only loses its words.
    const reason = answered ? 'failed' : (REASON_BY_STATUS.get(response.status) ?? 'failed');
    const text = yield* response.text.pipe(Effect.mapError(failedBy(reason)));
    if (answered) return text;
    return yield* refusedWith(reason, text);
  }).pipe(
    Effect.provideService(FetchHttpClient.RequestInit, { credentials: 'include' }),
    Effect.provideService(HttpClient.TracerPropagationEnabled, false),
  );

const decoded = <A>(
  request: HttpClientRequest.HttpClientRequest,
  contract: Schema.Codec<A, unknown>,
): ApplicationsCall<A> =>
  Effect.flatMap(answeredText(request), (text) =>
    Schema.decodeEffect(Schema.fromJsonString(contract))(text, CLOSED).pipe(
      Effect.mapError(failedBy('invalid-reply')),
    ),
  );

const timed = <A>(call: ApplicationsCall<A>): ApplicationsCall<A> =>
  call.pipe(
    Effect.timeoutOrElse({ duration: READ_BUDGET, orElse: () => Effect.fail(failed('timed-out')) }),
  );

const deadlined = <A>(call: ApplicationsCall<A>): ApplicationsCall<A> =>
  call.pipe(
    Effect.timeoutOrElse({
      duration: WRITE_DEADLINE,
      orElse: () => Effect.fail(failed('timed-out')),
    }),
  );

const writeDecoded = <A>(
  request: HttpClientRequest.HttpClientRequest,
  contract: Schema.Codec<A, unknown>,
): ApplicationsCall<A> => deadlined(decoded(request, contract));

const jsonBody = (body: OpinApplicationCreateBody | OpinApplicationUpdateBody): HttpBody.HttpBody =>
  HttpBody.raw(JSON.stringify(body), { contentType: 'application/json' });

const applicationPath = (apiBaseUrl: string, clientId: string): string =>
  `${apiBaseUrl}${APPLICATIONS_PATH}/${encodeURIComponent(clientId)}`;

/** One applications call, against the Power API at the base URL the edge resolves. */
export type ApplicationsOperation<A> = (apiBaseUrl: string) => ApplicationsCall<A>;

/** Every participant and built-in application. */
export const listApplications: ApplicationsOperation<OpinApplicationListType> = (apiBaseUrl) =>
  timed(decoded(HttpClientRequest.get(`${apiBaseUrl}${APPLICATIONS_PATH}`), OpinApplicationList));

/**
 * The session operator's scopes, which every Admin screen gates its actions on; a session without
 * a grant answers `forbidden`.
 */
export const readSessionScopes: ApplicationsOperation<readonly string[]> = (apiBaseUrl) =>
  timed(
    Effect.flatMap(answeredText(HttpClientRequest.get(`${apiBaseUrl}${SESSION_PATH}`)), (text) =>
      decodeSessionScopes(text).pipe(
        Effect.map((session) => session.scopes),
        Effect.mapError(failedBy('forbidden')),
      ),
    ),
  );

/** Creates a participant application; the answer carries its secret, shown once. */
export const createApplication: {
  (
    idempotencyKey: OpinApplicationIdempotencyKey,
  ): (body: OpinApplicationCreateBody) => ApplicationsOperation<OpinApplicationSecretIssuedType>;
  (
    body: OpinApplicationCreateBody,
    idempotencyKey: OpinApplicationIdempotencyKey,
  ): ApplicationsOperation<OpinApplicationSecretIssuedType>;
} = dual(
  2,
  (
    body: OpinApplicationCreateBody,
    idempotencyKey: OpinApplicationIdempotencyKey,
  ): ApplicationsOperation<OpinApplicationSecretIssuedType> =>
    (apiBaseUrl) =>
      writeDecoded(
        HttpClientRequest.post(`${apiBaseUrl}${APPLICATIONS_PATH}`, {
          body: jsonBody(body),
          headers: { [FAPI_HEADERS.idempotencyKey]: idempotencyKey },
        }),
        OpinApplicationSecretIssued,
      ),
);

/** Issues a new secret for `clientId`; the previous one stops at once. */
export const rotateApplicationSecret: {
  (
    idempotencyKey: OpinApplicationIdempotencyKey,
  ): (clientId: string) => ApplicationsOperation<OpinApplicationSecretIssuedType>;
  (
    clientId: string,
    idempotencyKey: OpinApplicationIdempotencyKey,
  ): ApplicationsOperation<OpinApplicationSecretIssuedType>;
} = dual(
  2,
  (
    clientId: string,
    idempotencyKey: OpinApplicationIdempotencyKey,
  ): ApplicationsOperation<OpinApplicationSecretIssuedType> =>
    (apiBaseUrl) =>
      writeDecoded(
        HttpClientRequest.post(`${applicationPath(apiBaseUrl, clientId)}/secret`, {
          headers: { [FAPI_HEADERS.idempotencyKey]: idempotencyKey },
        }),
        OpinApplicationSecretIssued,
      ),
);

/** Renames `clientId` or changes its description. */
export const updateApplication: {
  (
    body: OpinApplicationUpdateBody,
  ): (clientId: string) => ApplicationsOperation<OpinApplicationViewType>;
  (
    clientId: string,
    body: OpinApplicationUpdateBody,
  ): ApplicationsOperation<OpinApplicationViewType>;
} = dual(
  2,
  (
    clientId: string,
    body: OpinApplicationUpdateBody,
  ): ApplicationsOperation<OpinApplicationViewType> =>
    (apiBaseUrl) =>
      writeDecoded(
        HttpClientRequest.patch(applicationPath(apiBaseUrl, clientId), { body: jsonBody(body) }),
        OpinApplicationView,
      ),
);

const switchApplication =
  (transition: 'enable' | 'disable') =>
  (clientId: string): ApplicationsOperation<OpinApplicationViewType> =>
  (apiBaseUrl) =>
    writeDecoded(
      HttpClientRequest.post(`${applicationPath(apiBaseUrl, clientId)}/${transition}`),
      OpinApplicationView,
    );

/** Enables `clientId`. */
export const enableApplication = switchApplication('enable');

/** Disables `clientId`; tokens already issued stay valid until they expire. */
export const disableApplication = switchApplication('disable');
